import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CLAIM_SETTLEMENT_DECISION,
  type ClaimSettlementDecision,
  type ClaimSettlementLineDto,
  type ClaimSettlementViewDto,
} from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { DataTable, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';

const DECISION_LABEL: Record<ClaimSettlementDecision, string> = {
  write_off: 'Write off',
  bill_patient: 'Bill the patient',
  credit_patient: 'Give back to the patient',
  resubmit: 'Send again',
  dispute: 'Dispute with the payer',
};

const MONEY_MOVING: ClaimSettlementDecision[] = ['bill_patient', 'credit_patient'];

interface DecisionDraft {
  decision: ClaimSettlementDecision;
  amount: string;
  reason: string;
  consentRef: string;
  followUpOn: string;
}

function ManualAnswer({ view, onDone }: { view: ClaimSettlementViewDto; onDone: () => void }) {
  const toast = useToast();
  const [approved, setApproved] = useState<Record<string, string>>(() =>
    Object.fromEntries(view.billedLines.map((line) => [line.invoiceItemId, line.approvedMinor === null ? '' : formatMoneyInput(line.approvedMinor)])),
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post<ClaimSettlementViewDto>(`/insurance/claims/${view.claimId}/answer`, {
        lines: view.billedLines
          .filter((line) => (approved[line.invoiceItemId] ?? '').trim() !== '')
          .map((line) => ({
            invoiceItemId: line.invoiceItemId,
            approvedMinor: minorFromInput(approved[line.invoiceItemId] ?? ''),
            ...(reasons[line.invoiceItemId]?.trim() ? { reasonCode: reasons[line.invoiceItemId].trim() } : {}),
          })),
      }),
    onSuccess: () => {
      toast.success('The payer’s answer is recorded');
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const entered = view.billedLines.filter((line) => (approved[line.invoiceItemId] ?? '').trim() !== '').length;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">What the payer approved, line by line</p>
      <p className="text-xs text-muted-foreground">From the payer’s letter, portal or payment advice. Leave a line empty until the payer has answered it.</p>
      <DataTable
        dense
        rows={view.billedLines}
        rowKey={(row) => row.invoiceItemId}
        columns={[
          { header: 'Bill', cell: (row) => <span className="tabular text-xs">{row.invoiceNumber}</span> },
          { header: 'Service', cell: (row) => row.description },
          { header: 'Claimed', align: 'right', cell: (row) => money(row.panelMinor) },
          {
            header: 'Approved',
            align: 'right',
            cell: (row) => (
              <Input
                className="w-28"
                type="text" dir="ltr" inputMode="decimal"
                value={approved[row.invoiceItemId] ?? ''}
                onChange={(e) => setApproved({ ...approved, [row.invoiceItemId]: e.target.value })}
              />
            ),
          },
          {
            header: 'Reason code',
            cell: (row) => <Input className="w-28" value={reasons[row.invoiceItemId] ?? ''} onChange={(e) => setReasons({ ...reasons, [row.invoiceItemId]: e.target.value })} />,
          },
        ]}
      />
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="flex justify-end">
        <Button size="sm" loading={save.isPending} disabled={entered === 0} onClick={() => save.mutate()}>
          Record {entered || ''} answer{entered === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
}

function LineDecision({ view, line, onDone }: { view: ClaimSettlementViewDto; line: ClaimSettlementLineDto; onDone: () => void }) {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();
  const proposal = view.proposals.find((row) => row.claimLineId === line.claimLineId);
  const [draft, setDraft] = useState<DecisionDraft>({
    decision: proposal?.decision ?? 'write_off',
    amount: proposal ? formatMoneyInput(proposal.amountMinor) : '',
    reason: '',
    consentRef: '',
    followUpOn: '',
  });
  const [error, setError] = useState<string | null>(null);
  const allowed = CLAIM_SETTLEMENT_DECISION.filter((decision) => isSuperAdmin || !MONEY_MOVING.includes(decision));

  const decide = useMutation({
    mutationFn: () =>
      post<ClaimSettlementViewDto>(`/insurance/claims/${view.claimId}/settle-lines`, {
        lines: [
          {
            claimLineId: line.claimLineId,
            decision: draft.decision,
            amountMinor: minorFromInput(draft.amount),
            reason: draft.reason.trim(),
            ...(draft.consentRef.trim() ? { consentRef: draft.consentRef.trim() } : {}),
            ...(draft.followUpOn ? { followUpOn: draft.followUpOn } : {}),
          },
        ],
      }),
    onSuccess: () => {
      toast.success('Decision recorded');
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-4">
      <p className="text-sm font-medium sm:col-span-4">
        {line.display} · {money(line.openMinor + line.creditOpenMinor)} undecided
        {proposal && <span className="ms-2 text-xs text-muted-foreground">Proposed: {DECISION_LABEL[proposal.decision]} ({humanize(proposal.family)})</span>}
      </p>
      <Field label="Decision">
        <Select value={draft.decision} onChange={(e) => setDraft({ ...draft, decision: e.target.value as ClaimSettlementDecision })}>
          {allowed.map((decision) => (
            <option key={decision} value={decision}>
              {DECISION_LABEL[decision]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Amount">
        <Input type="text" dir="ltr" inputMode="decimal" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
      </Field>
      {draft.decision === 'bill_patient' && (
        <Field label="Consent reference" hint={view.saRegime ? 'Required for a refused service' : undefined}>
          <Input value={draft.consentRef} onChange={(e) => setDraft({ ...draft, consentRef: e.target.value })} />
        </Field>
      )}
      {draft.decision === 'dispute' && (
        <Field label="Follow up on">
          <DatePicker value={draft.followUpOn} onChange={(value) => setDraft({ ...draft, followUpOn: value })} />
        </Field>
      )}
      <Field label="Reason" className="sm:col-span-4">
        <Input value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
      </Field>
      {error && (
        <div className="sm:col-span-4">
          <Alert variant="destructive">{error}</Alert>
        </div>
      )}
      <div className="flex justify-end sm:col-span-4">
        <Button size="sm" loading={decide.isPending} disabled={draft.reason.trim().length < 5 || !(minorFromInput(draft.amount) > 0)} onClick={() => decide.mutate()}>
          Record the decision
        </Button>
      </div>
    </div>
  );
}

export function ClaimSettlementPanel({ claimId, onChanged }: { claimId: string; onChanged: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, isSuperAdmin } = useAuth();
  const [closeReason, setCloseReason] = useState('');
  const queryKey = ['claim-settlement', claimId];
  const view = useQuery({ queryKey, queryFn: () => get<ClaimSettlementViewDto>(`/insurance/claims/${claimId}/settlement`) });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey });
    onChanged();
  };

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post(`/insurance/claims/${claimId}/settlements/${id}/reverse`, { reason }),
    onSuccess: () => {
      toast.success('Decision reversed');
      refresh();
    },
    onError: (caught) => toast.error('Could not reverse it', describeError(caught)),
  });

  const close = useMutation({
    mutationFn: () => post(`/insurance/claims/${claimId}/close`, { reason: closeReason.trim() }),
    onSuccess: () => {
      toast.success('Claim closed');
      refresh();
    },
    onError: (caught) => toast.error('Could not close the claim', describeError(caught)),
  });

  const data = view.data;
  if (!data) return null;
  const decidable = data.lines.filter((line) => line.openMinor > 0 || line.creditOpenMinor > 0);
  const decided = data.lines.flatMap((line) => line.decisions.map((decision) => ({ ...decision, display: line.display })));
  const showsSomething = data.manualAnswerAllowed || decidable.length > 0 || decided.length > 0;
  if (!showsSomething) return null;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Unpaid lines — decide who pays</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Asked of payer" value={money(data.askedOfPayerMinor)} />
        <StatTile label="Approved" value={money(data.approvedMinor)} />
        <StatTile label="Still to decide" value={money(decidable.reduce((sum, line) => sum + line.openMinor + line.creditOpenMinor, 0))} tone={decidable.length > 0 ? 'warning' : 'default'} />
      </div>
      {data.manualAnswerAllowed && can('claim.manage') && data.billedLines.length > 0 && <ManualAnswer key={data.lines.length} view={data} onDone={refresh} />}
      {can('claim.settle') && decidable.map((line) => <LineDecision key={`${line.claimLineId}-${line.openMinor}-${line.creditOpenMinor}`} view={data} line={line} onDone={refresh} />)}
      {!can('claim.settle') && decidable.length > 0 && <Alert>Accounts decides who pays what the payer did not.</Alert>}
      {decided.length > 0 && (
        <DataTable
          dense
          rows={decided}
          rowKey={(row) => row.id}
          columns={[
            { header: 'Line', cell: (row) => row.display },
            { header: 'Decision', cell: (row) => DECISION_LABEL[row.decision] },
            { header: 'Amount', align: 'right', cell: (row) => money(row.amountMinor) },
            { header: 'Reason', cell: (row) => <span className="text-xs text-muted-foreground">{row.reason}</span> },
            {
              header: '',
              cell: (row) =>
                row.reversedAt ? (
                  <Badge variant="muted">Reversed</Badge>
                ) : row.needsReview ? (
                  <Badge variant="warning">Insurer answered again</Badge>
                ) : null,
            },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                !row.reversedAt &&
                can('claim.settle') &&
                (isSuperAdmin || !MONEY_MOVING.includes(row.decision)) &&
                !['paid', 'rejected'].includes(data.status) && (
                  <Button size="sm" variant="ghost" loading={reverse.isPending && reverse.variables?.id === row.id} onClick={() => reverse.mutate({ id: row.id, reason: 'Reversed from the claim screen' })}>
                    Reverse
                  </Button>
                ),
            },
          ]}
        />
      )}
      {can('claim.settle') && ['approved', 'partially_paid'].includes(data.status) && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
          <Field label="Close the claim" hint="What is still unpaid is written off" className="min-w-64 flex-1">
            <Input value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="Why it can be closed" />
          </Field>
          <Button variant={data.readyToClose ? 'default' : 'outline'} loading={close.isPending} disabled={closeReason.trim().length < 5} onClick={() => close.mutate()}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
}

export function ReadyToCloseList({ onOpen }: { onOpen: (claimId: string) => void }) {
  const ready = useQuery({
    queryKey: ['claims-ready-to-close'],
    queryFn: () =>
      get<{ id: string; number: string; payer: { name: string }; approvedAmountMinor: number; paidAmountMinor: number; claimedAmountMinor: number }[]>('/insurance/claims/ready-to-close'),
  });
  if (!ready.data || ready.data.length === 0) return null;
  return (
    <div className="mb-4 space-y-2">
      <p className="text-sm font-medium">Claims ready to close</p>
      <DataTable
        dense
        rows={ready.data}
        rowKey={(row) => row.id}
        onRowClick={(row) => onOpen(row.id)}
        columns={[
          { header: 'Claim', cell: (row) => <span className="tabular">{row.number}</span> },
          { header: 'Payer', cell: (row) => row.payer.name },
          { header: 'Claimed', align: 'right', cell: (row) => money(row.claimedAmountMinor) },
          { header: 'Approved', align: 'right', cell: (row) => money(row.approvedAmountMinor) },
          { header: 'Paid', align: 'right', cell: (row) => money(row.paidAmountMinor) },
        ]}
      />
    </div>
  );
}
